import {
    AppSku,
    EntityType,
    FavoritesCollectionContract,
} from '@polaris-customerweb/sharedcomponents.core';
import { GeoBounds } from '@polaris-customerweb/sharedcomponents.shared';
import {
    Geofence,
    PlannedRide,
    TrackedRide,
    Waypoint,
} from '@polaris-customerweb/sharedcomponents.user-content';
import { cloneDeep } from 'lodash-es';
import { Observable, of } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import { ContentCollection } from './content-collection';

export class Favorite {
    protected contentCollection: ContentCollection;
    protected contentObservable$: Observable<ContentCollection> | undefined;
    protected rawCollectionBase: FavoritesCollectionContract;

    private _creationDate: Date;
    private _modificationDate?: Date;

    constructor(
        rawCollection: FavoritesCollectionContract = {},
        contentCollectionObservable?: Observable<ContentCollection>
    ) {
        this.rawCollectionBase = cloneDeep(rawCollection);
        this.contentObservable$ = contentCollectionObservable;

        if (this.rawCollectionBase.creationDate) {
            this._creationDate = new Date(this.rawCollectionBase.creationDate);
        }
        if (this.rawCollectionBase.modificationDate) {
            this._modificationDate = new Date(
                this.rawCollectionBase.modificationDate
            );
        }
    }

    get allGeofences(): readonly Geofence[] {
        return this.contentCollection?.allGeofences || [];
    }

    get allPlannedRides(): readonly PlannedRide[] {
        return this.contentCollection?.allPlannedRides || [];
    }

    get allTrackedRides(): readonly TrackedRide[] {
        return this.contentCollection?.allTrackedRides || [];
    }

    get allWaypoints(): readonly Waypoint[] {
        return this.contentCollection?.allWaypoints || [];
    }

    get appSku(): AppSku {
        return this.rawCollectionBase.appSku || 'Unknown';
    }

    get bounds(): GeoBounds {
        return this.contentCollection?.bounds || new GeoBounds();
    }

    get content(): ContentCollection {
        return this.contentCollection;
    }

    get creationDate(): Date {
        return this._creationDate;
    }

    get geofences(): readonly Geofence[] {
        return this.contentCollection?.geofences || [];
    }
    set geofences(geofences: readonly Geofence[]) {
        this.contentCollection.geofences = [...geofences];
        this.setLinkedItemsFromContent();
    }

    get hasItems(): boolean {
        return this.itemCount > 0;
    }

    get id(): string {
        return this.rawCollectionBase.id || '';
    }
    set id(id: string) {
        this.rawCollectionBase.id = id;
    }

    get isHydrated(): boolean {
        return Boolean(this.contentCollection);
    }

    get isOrv(): boolean {
        return this.contentCollection?.isOrv;
    }

    get isSno(): boolean {
        return this.contentCollection?.isSno;
    }

    get itemCount(): number {
        return this.rawCollectionBase.linkedItems?.length || 0;
    }

    get modificationDate(): Date | undefined {
        return this._modificationDate;
    }
    set modificationDate(modificationDate: Date | undefined) {
        const modificationDateString = modificationDate
            ? modificationDate.toISOString()
            : undefined;
        this.rawCollectionBase.modificationDate = modificationDateString;
    }

    get plannedRides(): readonly PlannedRide[] {
        return this.contentCollection?.plannedRides ?? ([] as PlannedRide[]);
    }
    set plannedRides(plannedRides: readonly PlannedRide[]) {
        this.contentCollection.plannedRides = [...plannedRides];
        this.setLinkedItemsFromContent();
    }

    get riderId(): string {
        return this.rawCollectionBase.riderId || '';
    }

    get trackedRides(): readonly TrackedRide[] {
        return this.contentCollection?.trackedRides || [];
    }
    set trackedRides(trackedRides: readonly TrackedRide[]) {
        this.contentCollection.trackedRides = [...trackedRides];
        this.setLinkedItemsFromContent();
    }

    get waypoints(): readonly Waypoint[] {
        return this.contentCollection?.waypoints || [];
    }
    set waypoints(waypoints: readonly Waypoint[]) {
        this.contentCollection.waypoints = [...waypoints];
        this.setLinkedItemsFromContent();
    }

    getContentCollection(): Observable<ContentCollection> {
        const result$ =
            this.contentCollection || !this.contentObservable$
                ? of(this.contentCollection)
                : this.contentObservable$;

        return result$.pipe(
            take(1),
            tap((contentCollection) => {
                this.setContentCollection(contentCollection);
            })
        );
    }

    addUserContentArrays(
        geofences: Geofence[] = [],
        plannedRides: PlannedRide[] = [],
        trackedRides: TrackedRide[] = [],
        waypoints: Waypoint[] = []
    ): void {
        if (!this.content) {
            this.setContentCollection(new ContentCollection());
        }

        this.contentCollection.addUserContentArrays(
            geofences,
            plannedRides,
            trackedRides,
            waypoints
        );
        this.setLinkedItemsFromContent();
    }

    removeItem(id: string, entityType: EntityType): void {
        if (!this.content) {
            this.setContentCollection(new ContentCollection());
        }

        this.contentCollection.removeItem(id, entityType);
        this.setLinkedItemsFromContent();
    }

    setContentCollection(
        contentCollection: ContentCollection,
        bounds?: GeoBounds
    ): void {
        this.contentCollection = contentCollection;
        if (bounds) {
            this.contentCollection.bounds = bounds;
        }
    }

    toContract(): FavoritesCollectionContract {
        return cloneDeep(this.rawCollectionBase);
    }

    toGeoJson(): FavoritesCollectionContract {
        return this.toContract();
    }

    toJSON(): FavoritesCollectionContract {
        return this.toGeoJson();
    }

    updateModifiedDate() {
        this._modificationDate = undefined;
        this.rawCollectionBase.modificationDate = new Date().toISOString();
    }

    private setLinkedItemsFromContent(): void {
        this.rawCollectionBase.linkedItems = (
            this.content?.toLinkedItems() || []
        ).map((linkedItem) => linkedItem.toDto());
    }
}
